import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

import type { ZoneDriver } from './zoneDrivers';

export type ZoneStatus = 'active' | 'inactive';
export type ZoneStatusFilter = ZoneStatus | 'all';

export interface Zone {
  id: string;
  hubId: number;
  code: string;
  name: string;
  color: string;
  status: ZoneStatus;
  cellCount: number;
  /** Driver roster, priority-ordered — always embedded (lives on the zone row). */
  drivers: ZoneDriver[];
  /** Present only when fetched with `include=cells`; cellCount is then trustworthy. */
  h3Indexes?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ZoneCells {
  zoneId: string;
  count: number;
  h3Indexes: string[];
}

export interface CreateZonePayload {
  hubId: number;
  code: string;
  name: string;
  color: string;
}

export interface UpdateZonePayload {
  name?: string;
  color?: string;
  status?: ZoneStatus;
}

export interface CellConflict {
  h3Index: string;
  existingZone: {
    id: string;
    code: string;
    hubId: number;
  };
}

export interface AddCellsResult {
  added: number;
}

export interface RemoveCellsResult {
  removed: number;
}

export class ZoneCellConflictError extends Error {
  conflicts: CellConflict[];
  constructor(conflicts: CellConflict[]) {
    super(
      `Cannot add ${conflicts.length} cell(s) — already owned by another zone`
    );
    this.name = 'ZoneCellConflictError';
    this.conflicts = conflicts;
  }
}

type Envelope<T> = { status: string; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

/** Zone tally of one hub — `GET /v1/zones/hubs`. */
export interface ZoneHubSummary {
  hubId: number;
  zoneCount: number;
  activeZoneCount: number;
}

export const zonesAPI = {
  /**
   * Which hubs actually have zones, in one request. The picker lists every
   * hub and client pitstop it can reach — hundreds of them — so this is what
   * separates the handful ops works with from the rest of the masterdata.
   */
  hubSummary: async (): Promise<ZoneHubSummary[]> => {
    try {
      const response = await Axios.get('/v1/zones/hubs');
      return unwrap<{ items: ZoneHubSummary[] }>(response.data).items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listByHub: async (
    hubId: number,
    status: ZoneStatusFilter = 'active',
    // 'cells' embeds each zone's h3Indexes — one request instead of a
    // per-zone GET /zones/:id/cells loop. Drivers are always embedded.
    include?: 'cells' | 'cells,drivers'
  ): Promise<Zone[]> => {
    try {
      const response = await Axios.get(`/v1/hubs/${hubId}/zones`, {
        params: include ? { status, include } : { status },
      });
      const data = unwrap<{ items: Zone[] }>(response.data);
      return data.items;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  getById: async (id: string): Promise<Zone> => {
    try {
      const response = await Axios.get(`/v1/zones/${id}`);
      return unwrap<Zone>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  create: async (payload: CreateZonePayload): Promise<Zone> => {
    try {
      const response = await Axios.post('/v1/zones', payload);
      return unwrap<Zone>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  update: async (id: string, payload: UpdateZonePayload): Promise<Zone> => {
    try {
      const response = await Axios.patch(`/v1/zones/${id}`, payload);
      return unwrap<Zone>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await Axios.delete(`/v1/zones/${id}`);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listCells: async (id: string): Promise<ZoneCells> => {
    try {
      const response = await Axios.get(`/v1/zones/${id}/cells`);
      return unwrap<ZoneCells>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  addCells: async (
    id: string,
    h3Indexes: string[]
  ): Promise<AddCellsResult> => {
    try {
      const response = await Axios.post(`/v1/zones/${id}/cells`, {
        h3Indexes,
      });
      return unwrap<AddCellsResult>(response.data);
    } catch (error: any) {
      const data = error?.response?.data;
      const payload =
        data && typeof data === 'object' && 'error' in data
          ? data
          : (data?.data ?? null);
      if (payload?.error === 'CELL_CONFLICT') {
        throw new ZoneCellConflictError(payload.conflicts ?? []);
      }
      throw new Error(getBackendErrorMessage(error));
    }
  },

  removeCells: async (
    id: string,
    h3Indexes: string[]
  ): Promise<RemoveCellsResult> => {
    try {
      const response = await Axios.delete(`/v1/zones/${id}/cells`, {
        data: { h3Indexes },
      });
      return unwrap<RemoveCellsResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};
